import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { auditLogKeys, customFieldKeys, estimateKeys } from '@/lib/queryKeys';

type CustomFieldValuePayload = { fieldDefinitionId: string; value: unknown };

/**
 * NOT-247 — Save an estimate's custom field values via the estimate-specific
 * `PATCH /estimates/:id/custom-field-values` endpoint (status-agnostic). Unlike
 * the generic custom-fields endpoint, this path runs the estimate side effects:
 * post-send edits are audited and the active PDF is only superseded when a
 * customer-facing value changes pre-send. Used by the editable Custom Fields
 * card on the estimate detail.
 */
export function useSaveEstimateCustomFieldValues(estimateId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<void, CustomFieldValuePayload[]>({
    mutationFn: (values) =>
      apiPatch<void>(operationsClient, opsEstimateEndpoints.customFieldValues(estimateId), {
        values,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: customFieldKeys.values('estimate', estimateId),
      });
      void queryClient.invalidateQueries({
        queryKey: auditLogKeys.byEntity('estimate', estimateId),
      });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.detail(estimateId) });
      toast.success('Custom fields saved');
    },
  });
}
